import { TransformFeature } from '@kb3d/models/classes/features/transform-feature';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import { IKbVector } from '@kb3d/models/classes/kb3d-models';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';

// This is passed to the worker as additional information needed
export type TransformWorkerPayload = {
    pivot: IKbVector;
};

// This is processed and deserialized in the worker based on the worker payload
export type TransformDependencyData = {
    pivot: Vector3;
};

export class TransformGeometryProcessor extends FeatureGeometryProcessor<TransformFeature, TransformDependencyData> {
    readWorkerPayload(_: WorkerProcessorHost, data: TransformWorkerPayload) {
        return {
            pivot: new Vector3(data.pivot.x ?? 0, data.pivot.y ?? 0, data.pivot.z ?? 0),
        };
    }

    updateMeshGeometry(
        feature: TransformFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<TransformDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);
        const meshPivot = dependencyData.data?.pivot;

        if (newVertexData.positions && newVertexData.positions.length > 0) {
            const newMeshVertexPositions = newVertexData.positions;
            const indexCount = newMeshVertexPositions.length / 3;
            let pivot: Vector3 | undefined;
            if (!meshPivot) {
                console.error('MoveVerticesGeometryProcessor: Mesh pivot point is not provided');
                pivot = Vector3.Zero();
            } else {
                pivot = meshPivot;
            }
            const vertexPosition = new Vector3();

            let scaleVector: Vector3 | undefined;
            if (!feature.scale.equal({ x: 1, y: 1, z: 1 })) {
                scaleVector = feature.scale.toVec3();
            }

            for (let i = 0; i < indexCount; i++) {
                if (pivot && scaleVector) {
                    vertexPosition.copyFromFloats(
                        newMeshVertexPositions[i * 3] - pivot.x,
                        newMeshVertexPositions[i * 3 + 1] - pivot.y,
                        newMeshVertexPositions[i * 3 + 2] - pivot.z
                    );

                    if (scaleVector) {
                        vertexPosition.multiplyInPlace(scaleVector);
                    }

                    vertexPosition.addInPlace(pivot);

                    newMeshVertexPositions[i * 3] = vertexPosition.x;
                    newMeshVertexPositions[i * 3 + 1] = vertexPosition.y;
                    newMeshVertexPositions[i * 3 + 2] = vertexPosition.z;
                }
            }
        }

        return newVertexData;
    }
}
